import { useRouter } from "next/router";
import { useEffect } from "react";
import { AnyAction, Store } from "redux";
import Cookies from "js-cookie";
import { BUY_FLOW, BUY_FLOW_COOKIE_KEY } from "~/constants/buyFlow";
import { PAGE_ROUTES } from "~/constants/pages";
import { ReduxState } from "~/types/redux";
import clientSide from "~/helpers/clientSide";
import { setCookie } from "~/helpers/cookie";
import { getOrCreateProcessId } from "~/helpers/tracking/cartFlow";
import { setCurrentlyInNGC } from "~/redux/actions/common";

/**
 * Hook to track and update the currentlyInNGC state based on the current route.
 *
 * As a side effect, writes the buyFlow cookie to "NGC" whenever the user lands on
 * an NGC-only route and the cookie hasn't been set yet (or was set to BAU). This
 * covers sessions where pageViewed never fires (~10%), which means Cohesion never
 * becomes ready, Monarch never evaluates the addressCheck slot, and useBuyFlowDecision
 * would otherwise leave the buyFlow cookie unset for the entire session. Without this,
 * every Tagular event in such sessions carries a BAU-prefixed processId even when the
 * user is actively in NGC.
 *
 * Route correction is limited to NGC-exclusive routes. Shared checkout routes rely
 * on the existing buyFlow cookie so BAU mobile checkout sessions are not re-labeled.
 */
const useRouteNGCTracker = (store: Store<ReduxState, AnyAction>): void => {
  const router = useRouter();

  useEffect(() => {
    if (!clientSide) return;

    const handleRouteChange = (url: string) => {
      const normalizedPath = url
        .split(/[?#]/)[0]
        .replace(/^\/buy/, "")
        .replace(/\/$/, "");
      const isNGCExclusiveRoute = [
        PAGE_ROUTES.BUILD,
        PAGE_ROUTES.RECOMMENDATION,
        PAGE_ROUTES.RECOMMENDATION_PROFILE,
        PAGE_ROUTES.QUIZ,
      ].includes(normalizedPath);
      const isSharedCheckoutRoute = [PAGE_ROUTES.CHECKOUT, PAGE_ROUTES.INSTALLATION].includes(normalizedPath);
      const buyFlowCookie = Cookies.get(BUY_FLOW_COOKIE_KEY);
      const isNGCRoute = isNGCExclusiveRoute || (isSharedCheckoutRoute && buyFlowCookie === BUY_FLOW.NGC);

      if (store.getState().currentlyInNGC !== isNGCRoute) {
        store.dispatch(setCurrentlyInNGC(isNGCRoute));
      }

      /*
       * Only NGC-exclusive routes may authoritatively correct buyFlow=NGC.
       * /checkout and /installation are shared: BAU mobile and some BAU journeys use
       * them too, so overwriting a BAU cookie there misattributes BAU sessions to NGC.
       */
      const cookieNeedsNGCCorrection = isNGCExclusiveRoute && buyFlowCookie !== BUY_FLOW.NGC;
      if (cookieNeedsNGCCorrection) {
        setCookie(BUY_FLOW_COOKIE_KEY, BUY_FLOW.NGC, { otGroup: "C0003" });
        getOrCreateProcessId();
      }
    };

    handleRouteChange(router.pathname);

    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router, store]);
};

export default useRouteNGCTracker;
